import { v4 as uuidv4 } from 'uuid';

import {
  isTokenExpired,
  storeTokenExpiration,
} from '@/components/shared/getToken';

import { setTokenInCookies } from '@/config/appwrite';

let refreshTimeout: NodeJS.Timeout | null = null;
let heartbeatInterval: NodeJS.Timeout | null = null;
let isRefreshing = false;
let retryCount = 0;

const tabId = uuidv4();
const channel = new BroadcastChannel('auth');

let globalLogoutHandler: (() => void) | null = null;

export const setGlobalLogoutHandler = (handler: (() => void) | null) => {
  globalLogoutHandler = handler;
};

const isLeader = (): boolean => {
  return localStorage.getItem('auth_leader') === tabId;
};

const ensureLeadership = () => {
  const leader = localStorage.getItem('auth_leader');
  const lastHeartbeat = parseInt(
    localStorage.getItem('auth_leader_heartbeat') || '0',
    10,
  );

  if (!leader || Date.now() - lastHeartbeat > 2 * 60 * 1000) {
    localStorage.setItem('auth_leader', tabId);
  }
};

const refreshToken = async () => {
  const expirationTime = localStorage.getItem('auth_token_expiration');
  if (!expirationTime) {
    return;
  }

  if (parseInt(expirationTime, 10) <= Date.now()) {
    localStorage.removeItem('auth_leader');
    localStorage.removeItem('auth_leader_heartbeat');
    localStorage.removeItem('auth_token_expiration');
    return;
  }

  if (isRefreshing || !isLeader()) return;

  isRefreshing = true;
  try {
    const token = await setTokenInCookies();

    if (token) {
      retryCount = 0;
      storeTokenExpiration();
      scheduleTokenRefresh();

      channel.postMessage({ type: 'refresh', token });
    }
  } catch (err) {
    retryCount++;

    if (retryCount > 5) {
      handleRefreshFailure();
      return;
    }

    const delay = Math.min(2 ** retryCount * 1000, 5 * 60 * 1000);
    refreshTimeout = setTimeout(refreshToken, delay);
  } finally {
    isRefreshing = false;
  }
};

export const scheduleTokenRefresh = () => {
  if (typeof window === 'undefined') return;

  const expirationTime = localStorage.getItem('auth_token_expiration');
  if (!expirationTime) return;

  const exp = parseInt(expirationTime, 10);
  if (isNaN(exp)) return;

  const expiresIn = exp - Date.now();
  if (refreshTimeout) clearTimeout(refreshTimeout);

  if (expiresIn <= 0) {
    refreshToken();
    return;
  }

  const refreshIn = Math.max(expiresIn - 2 * 60 * 1000, 5000);
  refreshTimeout = setTimeout(refreshToken, refreshIn);
};

const startHeartbeat = () => {
  if (heartbeatInterval) clearInterval(heartbeatInterval);

  heartbeatInterval = setInterval(() => {
    localStorage.setItem('auth_leader_heartbeat', Date.now().toString());
    ensureLeadership();

    if (isLeader() && isTokenExpired()) {
      refreshToken();
    }
  }, 60 * 1000);
};

channel.onmessage = (event) => {
  if (event.data.type === 'refresh') {
    storeTokenExpiration();
    scheduleTokenRefresh();
  }

  if (event.data.type === 'logout') {
    clearTokenRefresher();
    localStorage.removeItem('auth_token_expiration');

    if (globalLogoutHandler) {
      globalLogoutHandler();
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    }
  }
};

const handleRefreshFailure = () => {
  localStorage.removeItem('auth_leader');
  localStorage.removeItem('auth_leader_heartbeat');
  // Optionally: window.location.href = '/login?expired=true';
};

export const initTokenRefresher = () => {
  if (typeof window === 'undefined') return;

  ensureLeadership();
  scheduleTokenRefresh();
  startHeartbeat();

  window.addEventListener('beforeunload', () => {
    if (isLeader()) {
      localStorage.removeItem('auth_leader');
      localStorage.removeItem('auth_leader_heartbeat');
    }
  });
};

export const clearTokenRefresher = () => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }

  localStorage.removeItem('auth_leader');
  localStorage.removeItem('auth_leader_heartbeat');
  localStorage.removeItem('auth_token_expiration');

  channel.close();
};
